<script lang="ts" setup>
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import { isNil } from 'lodash-es';
import { useInternational } from '@/common/locale';

interface Props {
  evaluateValue?: string;
  duringTimeInterval: string;
  comparator: string;
  warningValue?: number | string;
  criticalValue?: number | string;
  unit?: string;
  labelWidth?: string;
  showLabel?: boolean;
  alertLevel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  evaluateValue: '',
  warningValue: undefined,
  criticalValue: undefined,
  unit: '',
  labelWidth: '116px',
  showLabel: true,
  alertLevel: undefined,
});

const { t } = useInternational();
</script>

<template>
  <h-stack
    class="threshold-display"
    :spacing="12"
  >
    <span
      v-if="props.showLabel"
      class="threshold-display__label"
      >{{ t('WORD.THRESHOLD') }}</span
    >
    <span v-if="props.evaluateValue">{{ props.evaluateValue }}</span>
    <span
      v-if="props.duringTimeInterval"
      class="threshold-display__during-text"
      >{{ t('WORD.DURING_THE') }}</span
    >
    <span v-if="props.duringTimeInterval">{{ props.duringTimeInterval }}</span>
    <span v-if="comparator">{{ props.comparator }}</span>
    <template v-if="props.alertLevel">
      <span v-if="props.alertLevel.toLowerCase() === 'warning' && !isNil(props.warningValue)">{{
        props.warningValue
      }}</span>
      <span
        v-else-if="props.alertLevel.toLowerCase() === 'critical' && !isNil(props.criticalValue)"
        >{{ props.criticalValue }}</span
      >
    </template>
    <template v-else>
      <v-stack
        v-if="!isNil(props.warningValue)"
        :spacing="8"
        align="center"
      >
        <status-tag type="warning">Warning</status-tag>
        <span>{{ props.warningValue }}</span>
      </v-stack>
      <v-stack
        v-if="!isNil(props.criticalValue)"
        :spacing="8"
        align="center"
      >
        <status-tag type="critical">Critical</status-tag>
        <span>{{ props.criticalValue }}</span>
      </v-stack>
    </template>
    <span v-if="props.unit">{{ props.unit }}</span>
  </h-stack>
</template>

<style scoped lang="scss">
.threshold-display {
  flex-wrap: wrap;
  row-gap: 8px;
  align-content: flex-start;
  align-items: center;
  height: auto !important;
  min-height: 38px;

  > span:not(.threshold-display__label) {
    white-space: nowrap;
  }

  &__label {
    flex-shrink: 0;
    min-width: v-bind('props.labelWidth');
    font-weight: 500;
    white-space: nowrap;
  }

  &__during-text {
    color: var(--color-gray-02-10);
    white-space: nowrap;
  }

  > :deep(.v-stack) {
    flex-shrink: 0;
  }
}
</style>
